import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  IntrinsicImage,
  Link,
  Section,
  SxStyleProp,
  Text,
  type TishmanThemeName,
} from '@tishman/components';
import { H } from '@hzdg/sectioning';
import React from 'react';
import { navigate } from 'gatsby';

import { formatStoryCategory } from '../../utils';

import { PrivateEventsImageCaption } from './PrivateEventsImageCaption';

type TPrivateEventsNews = {
  items: Queries.SanityStory[];
  paddingBottom?: number | string;
  showReadTheMagazineButton?: boolean;
  theme?: TishmanThemeName;
  sx?: SxStyleProp;
};

export const PrivateEventsNews = ({
  items,
  paddingBottom = 6,
  showReadTheMagazineButton = true,
  theme = 'Rock Center Black',
  sx = {},
}: TPrivateEventsNews) => {
  return (
    <Section id="news" sx={{ py: 6, pb: paddingBottom, ...sx }} theme={theme}>
      <Container>
        <Flex
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: ['22px', 4],
          }}
        >
          <H
            sx={{
              variant: 'styles.h2',
              fontFamily: 'headingSecondary',
              fontSize: ['30px', '46px'],
              letterSpacing: [2, 0],
            }}
          >
            Our Events in the News
          </H>
          <Box sx={{ display: ['none', 'block'] }}>
            <Link href="/magazine" variant="underline">
              SEE ALL
            </Link>
          </Box>
        </Flex>
        <Box sx={{ display: ['auto', 'none'], mb: '32px' }}>
          <Link href="/magazine" variant="underline">
            SEE ALL
          </Link>
        </Box>
        <Grid columns={[1, 3]} gap={3} sx={{ display: ['block', 'grid'] }}>
          {items?.map((item, index) => (
            <Link
              href={`/magazine/${formatStoryCategory(item?.category ?? '')}/${
                item?.titleAndSlug?.slug?.current
              }`}
              key={index}
              sx={{ textDecoration: 'none', color: 'inherit', mb: ['20px', 0] }}
            >
              <Box>
                {item?.poster?.asset?.gatsbyImageData && (
                  <Box sx={{ position: 'relative' }}>
                    <IntrinsicImage
                      alt={item?.poster?.alt ?? ''}
                      image={item?.poster?.asset?.gatsbyImageData}
                      ratio={1}
                      sx={{ height: ['228px', '246px'], maxHeight: '228px' }}
                    />
                    {item?.poster?.caption && (
                      <PrivateEventsImageCaption text={item.poster.caption} />
                    )}
                  </Box>
                )}
                <Flex
                  sx={{ justifyContent: 'space-between', my: ['26px', '24px'] }}
                >
                  <Text
                    sx={{ fontSize: 1, fontWeight: 500, letterSpacing: '1px' }}
                  >
                    {item?.authors?.[0]}
                  </Text>
                  <Text sx={{ fontSize: 1, fontWeight: 500 }}>
                    {item?.publishAt
                      ? ((): string => {
                          const date = new Date(item.publishAt);
                          const month = (date.getUTCMonth() + 1)
                            .toString()
                            .padStart(2, '0');
                          const day = date
                            .getUTCDate()
                            .toString()
                            .padStart(2, '0');
                          const year = date
                            .getUTCFullYear()
                            .toString()
                            .slice(-2);
                          return `${month}.${day}.${year}`;
                        })()
                      : ''}
                  </Text>
                </Flex>
                <Text
                  sx={{
                    fontSize: '24px',
                    fontWeight: '500',
                    lineHeight: '116%',
                    letterSpacing: 2,
                    marginBottom: ['36px', 0],
                  }}
                >
                  {item?.titleAndSlug?.title}
                </Text>
              </Box>
            </Link>
          ))}
        </Grid>
        {showReadTheMagazineButton && (
          <Box
            sx={{
              marginTop: '24px',
              marginBottom: '40px',
              display: ['none', 'flex'],
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              onClick={() => navigate('/magazine')}
              sx={{
                fontWeight: 'medium',
                mb: [2, 0],
                letterSpacing: 5,
                paddingTop: '21px',
                paddingBottom: '21px',
                paddingLeft: '12px',
                paddingRight: '12px',
              }}
              variant="buttons.primary"
            >
              Read the magazine
            </Button>
          </Box>
        )}
      </Container>
    </Section>
  );
};
